import * as React from 'react';
import type { TFunction } from 'i18next';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { INTERACTIVE_CELL_CLASSNAME, type ColumnDef } from '@ooh/ui/components/data-table';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import type { MediaUnitListItem, MediaUnitSortKey } from '@ooh/contracts';
import { formatPrice, formatArea, capitalize } from '@ooh/ui/lib/media-unit-formatters';
import { buildExtendedColumns } from './columns-extended';
import { SELECT_COLUMN_ID } from './column-ids';

export {
  COST_COLUMN_IDS,
  DEFAULT_HIDDEN_COLUMNS,
  OVERSCAN,
  ROW_ESTIMATE_PX,
  SELECT_COLUMN_ID,
  SUPPLIER_DETAIL_COLUMN_IDS,
} from './column-ids';
import { getMediaTypeLabel } from '../../../lib/map-config';
import {
  renderMediaUnitDimensions,
  renderMediaUnitNoPhotoHeader,
  renderMediaUnitPhotoStatus,
  renderMediaUnitText,
} from '../../../lib/media-unit-field-renderers';

/**
 * Media-unit catalog column definition: the shared `@ooh/ui` `ColumnDef` plus one
 * catalog-specific field (`toggleLabel`) used only by the `ColumnPicker` dropdown for
 * columns whose header `label` is intentionally blank (icon-only columns).
 *
 * Narrows `sortKey` from `@ooh/ui`'s generic `string` to `MediaUnitSortKey` — `@ooh/ui`
 * must stay contracts-free (see packages/ui/AGENTS.md), but at this app-level boundary
 * a typo'd `sortKey` literal in this file or columns-extended.tsx now fails the build
 * instead of silently compiling (qual-dry-violation, PR #964 review).
 */
export interface MediaUnitColumnDef extends Omit<ColumnDef<MediaUnitListItem>, 'sortKey'> {
  sortKey?: MediaUnitSortKey;
  /**
   * Optional human-readable name shown in the column-visibility menu when `label`
   * is intentionally blank (icon-only columns). Falls back to `label` when unset.
   */
  toggleLabel?: string;
}

export const alignClass = (align: 'left' | 'right' | 'center') =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

/**
 * Wraps an already-rendered cost/supplier cell in `MaskedValue`, masking only
 * when this specific value is null AND the caller-supplied `withheld` marker
 * is true. `withheld` MUST be the marker for THIS field's own dimension —
 * `item.costsWithheld` for a cost column, `item.supplierWithheld` for a
 * supplier column — never a caller-level `canViewCosts`/`canViewSupplierDetails`
 * flag, and never the other dimension's marker (field-visibility: "Restricted
 * values are masked, not removed from the layout"; "The two permissions are
 * resolved independently"). A genuinely-absent value on a fully-visible row
 * renders unmasked. Thin re-export of the shared `@ooh/ui` predicate —
 * kept under this name since every call site in this file and
 * `columns-extended.tsx` already imports it as `maskRestrictedCell`.
 */
export const maskRestrictedCell = maskFieldValue;

export interface SelectionColumnOptions {
  selectedIds: Set<number>;
  allRowsSelected: boolean;
  someRowsSelected: boolean;
  onToggleAll: () => void;
  onToggleRow: (id: number) => void;
  headerAriaLabel: string;
  getRowAriaLabel: (item: MediaUnitListItem) => string;
}

interface BuildColumnsOptions {
  selection?: SelectionColumnOptions;
  /** When false, omit cost columns (default: true) */
  canViewCosts?: boolean;
  /** When false, omit the supplierNumber column (default: true) */
  canViewSupplierDetails?: boolean;
  /**
   * `map`-namespaced translator for the mediaType column's `legend.types.*` labels
   * (same lookup the detail sidebar uses via `getMediaTypeLabel`). When omitted, the
   * column falls back to the raw `mediaType` code, capitalized.
   */
  tMap?: TFunction<'map'>;
  /**
   * Opens the full-screen photo preview for a has-photo row's `noPhoto` column
   * indicator. Omitted (no-op) is never expected in practice — `useMediaUnitsPage`
   * always wires this — but the column stays inert rather than throwing if unset.
   */
  onOpenPhotoPreview?: (unitId: number) => void;
  /**
   * Id of the unit whose photo-preview detail query is currently loading. The
   * matching row's has-photo indicator disables itself while its own preview
   * data is still in flight, so only that row shows a busy state.
   */
  photoPreviewLoadingUnitId?: number | null;
}

/**
 * Cell renderer for the `noPhoto` column, extracted out of `buildColumns` so its
 * `options?.onOpenPhotoPreview`/`options?.photoPreviewLoadingUnitId` reads live in
 * their own function scope instead of pushing `buildColumns` itself over the
 * cyclomatic-complexity lint budget. Pure render logic, no behavior change.
 */
function renderNoPhotoColumnCell(
  item: MediaUnitListItem,
  t: TFunction<'inventory', undefined>,
  options: BuildColumnsOptions | undefined,
): React.ReactNode {
  return renderMediaUnitPhotoStatus(
    item.firstPhotoUrl,
    { noPhotoAria: t('table.noPhotoAria'), hasPhotoAria: t('table.hasPhotoAria') },
    () => options?.onOpenPhotoPreview?.(item.id),
    options?.photoPreviewLoadingUnitId === item.id,
  );
}

interface SelectionClickCellProps {
  testId: string;
  onToggle: () => void;
  children: React.ReactNode;
}

/**
 * Shared "click anywhere in the cell to toggle" wrapper for both the selection
 * column's per-row cell and its header "select all" cell (checkbox-cell-click-area).
 * Defined at module scope (not inside `buildColumns`) so it keeps a stable component
 * identity across re-renders/rebuilds instead of remounting its subtree.
 *
 * `absolute inset-0` fills the `<td>`/`<th>` edge to edge, independent of the row's
 * content-driven height — the column def's `cellClassName`/`headerClassName` (see
 * `selectionColumn` below) makes the `<td>`/`<th>` itself `relative` so this resolves
 * against the cell's real containing block, not an unresolvable `h-full`/`w-full`.
 *
 * `stopPropagation` always blocks the row-link stretched-link overlay (row cell) /
 * header sort-click (header cell); the `target === currentTarget` check is what
 * avoids double-toggling — a click landing on the inner `Checkbox` bubbles here too,
 * but Radix's own `onCheckedChange` already handled that click, so this only
 * re-toggles when the click landed on the cell itself, outside the checkbox's small
 * hit target.
 */
function SelectionClickCell({ testId, onToggle, children }: SelectionClickCellProps) {
  return (
    <div
      className="absolute inset-0 flex cursor-pointer items-center justify-center"
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        if (event.target === event.currentTarget) {
          onToggle();
        }
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  );
}

export function buildColumns(
  t: TFunction<'inventory', undefined>,
  tCommon: TFunction<'common', undefined>,
  options?: BuildColumnsOptions,
): MediaUnitColumnDef[] {
  const selection = options?.selection;
  const tMap = options?.tMap;
  // Cost/supplier columns stay present for every caller (field-visibility);
  // masking is decided per row via maskRestrictedCell, never from these
  // caller-level flags. Accepted for backward call-site compatibility.
  const maskedExplanation = t('table.maskedExplanation');

  const baseColumns: MediaUnitColumnDef[] = [
    {
      id: 'noPhoto',
      label: '',
      // Header renders an icon via renderHeader below (blank text label); the
      // visibility menu uses this name instead.
      toggleLabel: t('table.columns.noPhoto'),
      align: 'center',
      width: 64,
      // Shrinks the default 'px-4 py-3' cell padding so the has-photo thumbnail
      // button (40px, size="icon") fits both axes: vertically within the
      // virtualizer's ~48px row step (row-height-overflow fix — the shared
      // virtualizer positions rows by a fixed 48px estimate with no dynamic
      // remeasurement, so an oversized cell overlaps the next row: 40px button +
      // 4px total vertical padding = 44px, ~4px under the 48px ceiling), and
      // horizontally within the column's own width (64px column - 16px total
      // horizontal padding = 48px content area, a 4px margin around the 40px
      // button on each side — the column width was never widened when the
      // no-photo icon grew into a full-size clickable thumbnail button).
      cellClassName: 'px-2 py-0.5',
      renderHeader: () => renderMediaUnitNoPhotoHeader(t('table.columns.noPhoto')),
      renderCell: (item) => renderNoPhotoColumnCell(item, t, options),
    },
    {
      id: 'city',
      label: t('table.columns.city'),
      align: 'left',
      width: 110,
      sortable: true,
      sortKey: 'city',
      renderCell: (item) => <>{item.city}</>,
    },
    {
      id: 'region',
      label: t('table.columns.region'),
      align: 'left',
      width: 130,
      sortable: true,
      sortKey: 'region',
      renderCell: (item) => (
        <span className="truncate text-muted-foreground block">{capitalize(item.region)}</span>
      ),
    },
    {
      id: 'address',
      label: t('table.columns.address'),
      align: 'left',
      width: 180,
      sortable: true,
      sortKey: 'address',
      renderCell: (item) => (
        <span className="truncate text-muted-foreground block" style={{ maxWidth: 'inherit' }}>
          {item.address ?? '\u2014'}
        </span>
      ),
    },
    {
      id: 'supplierNumber',
      label: t('table.columns.supplierNumber'),
      align: 'left',
      width: 120,
      sortable: true,
      sortKey: 'supplierNumber',
      renderCell: (item) =>
        maskRestrictedCell(
          item.supplierNumber,
          item.supplierWithheld,
          maskedExplanation,
          <span className="font-mono text-xs">{item.supplierNumber ?? '\u2014'}</span>,
        ),
    },
    {
      id: 'mediaType',
      label: t('table.columns.mediaType'),
      align: 'left',
      width: 140,
      sortable: true,
      sortKey: 'mediaType',
      renderCell: (item) =>
        renderMediaUnitText(
          item.mediaType == null
            ? null
            : tMap
              ? getMediaTypeLabel(item.mediaType, tMap)
              : capitalize(item.mediaType),
        ),
    },
    {
      id: 'supplier',
      label: t('table.columns.supplier'),
      align: 'left',
      width: 140,
      sortable: true,
      sortKey: 'supplierName',
      renderCell: (item) =>
        maskRestrictedCell(
          item.supplierName,
          item.supplierWithheld,
          maskedExplanation,
          <span className="truncate text-muted-foreground block">
            {item.supplierName ?? '\u2014'}
          </span>,
        ),
    },
    {
      id: 'dimensions',
      label: t('table.columns.dimensions'),
      align: 'left',
      width: 120,
      sortable: true,
      // Composite server sort: widthM primary, heightM fixed secondary
      // tiebreak — see SORT_COLUMNS in packages/db's list-media-units.ts.
      sortKey: 'dimensions',
      renderCell: (item) =>
        renderMediaUnitDimensions(item.widthM, item.heightM, item.dimensionNotes),
    },
    {
      id: 'area',
      label: t('table.columns.area'),
      align: 'right',
      width: 80,
      sortable: true,
      sortKey: 'areaSqm',
      renderCell: (item) => <span className="font-mono text-xs">{formatArea(item.areaSqm)}</span>,
    },
    {
      id: 'illuminated',
      label: t('table.columns.lighting'),
      align: 'center',
      width: 110,
      sortable: true,
      sortKey: 'illuminated',
      renderCell: (item) => (
        <span
          className={item.illuminated ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}
        >
          {item.illuminated ? tCommon('bool.yes') : tCommon('bool.no')}
        </span>
      ),
    },
    {
      id: 'catalogPrice',
      label: t('table.columns.catalogPrice'),
      align: 'right',
      width: 110,
      sortable: true,
      sortKey: 'catalogPrice',
      // catalogPrice is a client price, never gated by cost.view
      // (permission-model correction, CU-86cbgnt38 round 4) — never masked.
      renderCell: (item) => (
        <span className="font-mono text-xs">{formatPrice(item.catalogPrice)}</span>
      ),
    },
    ...buildExtendedColumns(t, maskedExplanation),
  ];

  const selectionColumn = selection
    ? ({
        id: SELECT_COLUMN_ID,
        label: '',
        align: 'center' as const,
        width: 44,
        // Structural column (only present in quick-add/bulk-edit mode) — excluded from
        // drag-reorder like the appended month columns; it is always pinned first via
        // `useColumnPreferences`'s `pinnedIds`, not user-reorderable.
        fixed: true,
        // Overrides the DataTable default `px-4 py-3` (packages/ui's data-table-rows.tsx
        // / table.tsx TableHead), which shrinks this 44px-wide column's content box to
        // ~12px — well under the 16px Checkbox — leaving an unreachable "dead" padding
        // ring around it whose clicks fall through to the bare `<td>`/`<th>` (no
        // stopPropagation there) and bubble to row navigation / do nothing
        // (checkbox-cell-click-area). Same precedent/values as the offer workspace's
        // units-tab-columns.tsx row-checkbox column.
        //
        // `relative` (row: via INTERACTIVE_CELL_CLASSNAME, header: bare) makes the
        // `<td>`/`<th>` itself the positioning context for the inner clickable div's
        // `absolute inset-0` — NOT `h-full`/`w-full` on the div, which cannot resolve
        // against a table cell's actual rendered height (a table cell has no
        // *specified* height for percentage children to resolve against, even though
        // its *used* height ends up taller than its content — e.g. a 2-line address in
        // a sibling column). `inset-0` instead resolves against the cell's real
        // containing block after layout, so it fills the full cell regardless of the
        // row's content-driven height.
        cellClassName: `${INTERACTIVE_CELL_CLASSNAME} px-1 py-2`,
        headerClassName: 'px-1 relative',
        // The `<td>`'s own `relative z-[1]` (from INTERACTIVE_CELL_CLASSNAME above) is
        // also what elevates this cell above the row-link stretched-link overlay (see
        // apps/web/src/routes/media-units/index.tsx's DataTable `renderRowLink`) so
        // clicking/dragging the checkbox always wins the hit-test, never navigation.
        renderHeader: () => (
          <SelectionClickCell
            testId="inventory-select-all-cell"
            onToggle={() => selection.onToggleAll()}
          >
            <Checkbox
              checked={
                selection.allRowsSelected
                  ? true
                  : selection.someRowsSelected
                    ? 'indeterminate'
                    : false
              }
              onCheckedChange={() => selection.onToggleAll()}
              data-testid="inventory-select-all"
              aria-label={selection.headerAriaLabel}
            />
          </SelectionClickCell>
        ),
        renderCell: (item: MediaUnitListItem) => (
          <SelectionClickCell
            testId={`inventory-select-row-${item.id}`}
            onToggle={() => selection.onToggleRow(item.id)}
          >
            <Checkbox
              checked={selection.selectedIds.has(item.id)}
              onCheckedChange={() => selection.onToggleRow(item.id)}
              data-testid={`inventory-row-checkbox-${item.id}`}
              aria-label={selection.getRowAriaLabel(item)}
            />
          </SelectionClickCell>
        ),
      } satisfies MediaUnitColumnDef)
    : null;

  // Cost/supplier columns stay present for every caller (field-visibility) —
  // their cells mask per row via maskRestrictedCell above, instead of the
  // column being omitted here.
  const visibleBase = [...baseColumns];

  if (selectionColumn != null) {
    visibleBase.unshift(selectionColumn);
  }

  return visibleBase;
}
